/**
 * CloudGMUsersTab — iter-267 (Feb 2026)
 * ======================================
 * Unified CloudGM customers list. Same 3-button layout as
 * `UsersAccountsTab` (merchant list):
 *
 *   🛠️ إدارة الحساب    → navigates to /manage-platform/cgc-users/:id
 *   🚫 حظر / فك حظر    → POST /admin/cgc-users/:id/{block|unblock}
 *   📦 أرشفة            → POST /admin/cgc-users/:id/archive
 *                        (disabled until the customer is blocked)
 *
 * Delete is intentionally missing — only the archive page can perform
 * a cascade delete (block → archive → delete lifecycle enforced by
 * the backend proxy).
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Search, RefreshCw, Loader2, User, Mail, Phone, Ban,
  RotateCcw, Archive, ShieldCheck, Calendar, X, Globe,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../../components/ui/dialog';
import { getAuthHeaders } from '../../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { id: 'all',     label_ar: 'الكل',    label_en: 'All' },
  { id: 'active',  label_ar: 'النشطون', label_en: 'Active' },
  { id: 'banned',  label_ar: 'المحظورون', label_en: 'Banned' },
];

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className={`p-4 rounded-xl bg-white/[0.02] border border-white/10`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[#71717A]">{label}</span>
        <Icon className={`h-4 w-4 text-${color}-400`} />
      </div>
      <p className={`text-2xl font-bold text-${color}-400`}>{value ?? '—'}</p>
    </div>
  );
}

function UserRow({ user, language, onManage, onBlock, onUnblock, onArchive, busy }) {
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const isBlocked = user.status === 'banned' || user.is_banned;
  const rowBusy = busy === user.id;

  return (
    <div
      className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e] hover:border-amber-500/30 transition-colors flex flex-col md:flex-row md:items-center gap-3"
      data-testid={`cloudgm-user-row-${user.id}`}
    >
      {/* Identity block */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border ${
          isBlocked
            ? 'bg-red-500/10 border-red-500/30'
            : 'bg-emerald-500/10 border-emerald-500/30'}`}>
          <User className={`h-5 w-5 ${isBlocked ? 'text-red-400' : 'text-emerald-400'}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-white truncate">
              {user.name || user.full_name
                || `${user.first_name || ''} ${user.last_name || ''}`.trim()
                || t('بدون اسم', 'No name')}
            </p>
            {isBlocked ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400">
                {t('محظور', 'Banned')}
              </span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
                {t('نشط', 'Active')}
              </span>
            )}
            {user.created_at && (
              <span className="text-[10px] text-[#606060] flex items-center gap-1">
                <Calendar className="h-2.5 w-2.5" />
                {new Date(user.created_at).toLocaleDateString()}
              </span>
            )}
          </div>
          {user.email && (
            <p className="text-xs text-[#A1A1AA] flex items-center gap-1 mt-0.5" dir="ltr">
              <Mail className="h-3 w-3 text-[#71717A] shrink-0" />
              <span className="truncate">{user.email}</span>
            </p>
          )}
          {user.phone && (
            <p className="text-xs text-[#A1A1AA] flex items-center gap-1" dir="ltr">
              <Phone className="h-3 w-3 text-[#71717A] shrink-0" />
              <span>{user.phone}</span>
            </p>
          )}
          {isBlocked && (user.banned_reason || user.ban_reason) && (
            <p className="text-xs text-red-400 mt-0.5 truncate">
              {t('سبب الحظر:', 'Ban reason:')} {user.banned_reason || user.ban_reason}
            </p>
          )}
        </div>
      </div>

      {/* 3 unified actions */}
      <div className="flex items-center gap-2 shrink-0 flex-wrap md:flex-nowrap md:justify-end">
        <Button
          size="sm"
          onClick={() => onManage(user)}
          className="bg-[#06b6d4]/15 hover:bg-[#06b6d4]/25 text-[#06b6d4] border border-[#06b6d4]/30"
          data-testid={`cgc-manage-btn-${user.id}`}
        >
          <ShieldCheck className="h-3.5 w-3.5 me-1" />
          {t('إدارة الحساب', 'Manage')}
        </Button>

        <Button
          size="sm"
          onClick={() => (isBlocked ? onUnblock(user) : onBlock(user))}
          disabled={rowBusy}
          className={isBlocked
            ? 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30'
            : 'bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30'}
          data-testid={`cgc-block-btn-${user.id}`}
        >
          {rowBusy ? (
            <Loader2 className="h-3.5 w-3.5 me-1 animate-spin" />
          ) : isBlocked ? (
            <RotateCcw className="h-3.5 w-3.5 me-1" />
          ) : (
            <Ban className="h-3.5 w-3.5 me-1" />
          )}
          {isBlocked ? t('إلغاء الحظر', 'Unblock') : t('حظر', 'Block')}
        </Button>

        <Button
          size="sm"
          onClick={() => onArchive(user)}
          disabled={rowBusy || !isBlocked}
          title={!isBlocked ? t('احظر الحساب أولاً', 'Block the account first') : ''}
          className={isBlocked
            ? 'bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30'
            : 'bg-white/[0.02] text-white/30 border border-white/10 cursor-not-allowed'}
          data-testid={`cgc-archive-btn-${user.id}`}
        >
          {rowBusy ? <Loader2 className="h-3.5 w-3.5 me-1 animate-spin" /> : <Archive className="h-3.5 w-3.5 me-1" />}
          {t('أرشفة', 'Archive')}
        </Button>
      </div>
    </div>
  );
}

export default function CloudGMUsersTab({ language }) {
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [busy, setBusy] = useState('');
  const [blockingUser, setBlockingUser] = useState(null);
  const [blockReasonId, setBlockReasonId] = useState('');
  const [blockOtherNotes, setBlockOtherNotes] = useState('');
  const [blockOtherNotesEn, setBlockOtherNotesEn] = useState('');
  const [translating, setTranslating] = useState(false);
  const [banReasons, setBanReasons] = useState([]);

  // Load CGC ban reasons when modal opens
  useEffect(() => {
    if (!blockingUser || banReasons.length > 0) return;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/dropdown-reasons/cgc_ban_reason`,
          { headers: getAuthHeaders() });
        setBanReasons((r.data?.data || []).filter((x) => x.is_active !== false));
      } catch (err) { setBanReasons([]); }
    })();
  }, [blockingUser, banReasons.length]);

  const selectedReason = banReasons.find((r) => r.id === blockReasonId);
  const isOtherSelected = !!(selectedReason && selectedReason.is_locked);

  // iter-270 — Bi-directional AI translate (ar↔en) for the notes.
  const translateOtherNotes = async () => {
    const ar = blockOtherNotes.trim();
    const en = blockOtherNotesEn.trim();
    const direction = ar ? 'ar_to_en' : (en ? 'en_to_ar' : null);
    if (!direction) { toast.error(t('اكتب الملاحظة أولاً', 'Enter a note first')); return; }
    setTranslating(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/translate`,
        { text: direction === 'ar_to_en' ? ar : en, direction },
        { headers: getAuthHeaders() });
      if (r.data?.translated) {
        if (direction === 'ar_to_en') setBlockOtherNotesEn(r.data.translated);
        else setBlockOtherNotes(r.data.translated);
        toast.success(t('تمت الترجمة', 'Translated'));
      }
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setTranslating(false); }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (tab === 'banned') params.set('status', 'banned');
      if (tab === 'active') params.set('status', 'active');
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/cloudgm-users?${params.toString()}`,
        { headers: getAuthHeaders() },
      );
      setUsers(res.data?.data || []);
      setStats(res.data?.stats || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [search, tab, t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ─── Handlers ────────────────────────────────────────────────────
  const openBlockModal = (user) => {
    setBlockingUser(user);
    setBlockReasonId('');
    setBlockOtherNotes('');
    setBlockOtherNotesEn('');
  };

  const submitBlock = async () => {
    if (!selectedReason) { toast.error(t('اختر سبب الحظر', 'Select a reason')); return; }
    if (isOtherSelected && !blockOtherNotes.trim() && !blockOtherNotesEn.trim()) {
      toast.error(t('اكتب الملاحظات بالعربية أو الإنجليزية', 'Enter notes in Arabic or English'));
      return;
    }
    const noteAr = blockOtherNotes.trim();
    const noteEn = blockOtherNotesEn.trim();
    const reason = isOtherSelected
      ? (noteAr || noteEn)
      : (selectedReason.label_ar || selectedReason.label_en || '');
    setBusy(blockingUser.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/cgc-users/${blockingUser.id}/block`,
        {
          reason,
          email: blockingUser.email,
          reason_id: selectedReason.id,
          reason_key: selectedReason.key || (selectedReason.is_locked ? 'other' : ''),
          reason_label_ar: selectedReason.label_ar,
          reason_label_en: selectedReason.label_en,
          note_ar: isOtherSelected ? noteAr : '',
          note_en: isOtherSelected ? noteEn : '',
          notes_en: isOtherSelected ? noteEn : '',
        },
        { headers: getAuthHeaders() },
      );
      setUsers((prev) => prev.map((u) => (
        u.id === blockingUser.id
          ? { ...u, status: 'banned', is_banned: true, ban_reason: reason, banned_reason: reason }
          : u
      )));
      toast.success(t('تم حظر الحساب', 'Account blocked'));
      setBlockingUser(null);
      setBlockReasonId('');
      setBlockOtherNotes('');
      setBlockOtherNotesEn('');
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy('');
    }
  };

  const handleUnblock = async (user) => {
    if (!window.confirm(t(`إلغاء حظر ${user.email}؟`, `Unblock ${user.email}?`))) return;
    setBusy(user.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/cgc-users/${user.id}/unblock`,
        {},
        { headers: getAuthHeaders() },
      );
      setUsers((prev) => prev.map((u) => (
        u.id === user.id
          ? { ...u, status: 'active', is_banned: false, ban_reason: '', banned_reason: '' }
          : u
      )));
      toast.success(t('تم إلغاء الحظر', 'Unblocked'));
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy('');
    }
  };

  const handleArchive = async (user) => {
    // iter-269 — no client-side gate. Backend enforces the rule; we
    // just surface any 4xx it returns.
    if (!window.confirm(t(`أرشفة ${user.email}؟`, `Archive ${user.email}?`))) return;
    setBusy(user.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/cgc-users/${user.id}/archive`,
        {},
        { headers: getAuthHeaders() },
      );
      // Row disappears from main list immediately.
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success(t('تمت الأرشفة', 'Archived'));
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy('');
    }
  };

  const handleManage = (user) => {
    const params = new URLSearchParams();
    if (user.email) params.set('email', user.email);
    navigate(`/manage-platform/cgc-users/${user.id}?${params.toString()}`);
  };

  return (
    <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6" data-testid="cloudgm-users-tab">
      {/* Header + search + refresh */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Users className="h-5 w-5 text-emerald-400" />
          {t('عملاء CloudGM المسجلون', 'CloudGM Registered Customers')}
        </h2>
        <div className="flex items-center gap-2 flex-1 sm:flex-none min-w-[260px] max-w-md">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('ابحث بالاسم/البريد/الهاتف...', 'Search by name/email/phone...')}
              className="w-full ps-3 pe-9 py-2 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm focus:border-emerald-500/50 focus:outline-none"
              data-testid="cloudgm-users-search"
            />
          </div>
          <Button
            onClick={fetchUsers}
            variant="outline"
            size="sm"
            className="border-white/10 text-white hover:bg-white/5"
            data-testid="cloudgm-users-refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stat cards (3 — archived is not shown here) */}
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="cloudgm-users-stats">
        <StatCard label={t('الإجمالي',  'Total')}  value={stats.total}  color="emerald" icon={Users} />
        <StatCard label={t('النشطون',   'Active')} value={stats.active} color="green"   icon={ShieldCheck} />
        <StatCard label={t('المحظورون', 'Banned')} value={stats.banned} color="red"     icon={Ban} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 bg-[#0a0a0a] p-1 rounded-xl border border-[#2a2a4e] w-fit flex-wrap">
        {STATUS_TABS.map((tt) => (
          <button
            key={tt.id}
            onClick={() => setTab(tt.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              tab === tt.id ? 'bg-emerald-500/15 text-emerald-400' : 'text-[#71717A] hover:text-white'
            }`}
            data-testid={`cloudgm-users-tab-${tt.id}`}
          >
            {t(tt.label_ar, tt.label_en)}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-400 mx-auto" />
          <p className="text-[#808080] mt-2">{t('جاري التحميل...', 'Loading...')}</p>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 text-[#808080]">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>{t('لا يوجد مستخدمون مطابقون', 'No users match')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((u, idx) => (
            <UserRow
              key={u.id || u.email || `cgc-row-${idx}`}
              user={u}
              language={language}
              onManage={handleManage}
              onBlock={openBlockModal}
              onUnblock={handleUnblock}
              onArchive={handleArchive}
              busy={busy}
            />
          ))}
        </div>
      )}

      {/* Block-reason modal — iter-269 dropdown + Other free-text + translate */}
      <Dialog open={!!blockingUser} onOpenChange={(o) => { if (!o) { setBlockingUser(null); setBlockReasonId(''); setBlockOtherNotes(''); setBlockOtherNotesEn(''); } }}>
        <DialogContent
          className="bg-[#0f0f1e] border-[#2a2a4e] text-white max-w-md"
          dir={isAr ? 'rtl' : 'ltr'}
          data-testid="cgc-block-reason-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-400" />
              {t('حظر الحساب', 'Block Account')}
            </DialogTitle>
            <DialogDescription className="text-sm text-white/60">
              {t(
                `اختر سبب حظر ${blockingUser?.email || ''}. لو اخترت "أخرى" اكتب الملاحظات ثم يمكنك ترجمتها.`,
                `Pick a reason for blocking ${blockingUser?.email || ''}. If you pick "Other" enter free-text notes and optionally translate them.`,
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-[11px] text-white/60 block">{t('السبب', 'Reason')}</label>
            <select
              value={blockReasonId}
              onChange={(e) => setBlockReasonId(e.target.value)}
              className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-red-400/60 focus:outline-none [&>option]:bg-[#0a0a15]"
              dir={isAr ? 'rtl' : 'ltr'}
              data-testid="cgc-block-reason-select"
            >
              <option value="" disabled>{t('— اختر سبباً —', '— Choose a reason —')}</option>
              {banReasons.map((r) => (
                <option key={r.id} value={r.id}>{isAr ? r.label_ar : (r.label_en || r.label_ar)}</option>
              ))}
            </select>
            {isOtherSelected && (
              <div className="space-y-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.03] p-3">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] text-amber-300">{t('ملاحظات (يظهر للعميل)', 'Notes (shown to the customer)')}</label>
                  <Button size="sm" variant="ghost" onClick={translateOtherNotes}
                          disabled={translating || (!blockOtherNotes.trim() && !blockOtherNotesEn.trim())}
                          className="h-6 px-2 text-[10px] text-cyan-300 hover:bg-cyan-500/10"
                          data-testid="cgc-block-other-translate-btn"
                          title={t('ترجم النص المكتوب — من العربي للإنجليزي أو العكس', 'Translate whichever box has text')}>
                    {translating ? <Loader2 className="h-3 w-3 animate-spin me-1" /> : <Globe className="h-3 w-3 me-1" />}
                    {t('ترجم بالذكاء الاصطناعي', 'AI Translate')}
                  </Button>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-white/50 flex items-center gap-1">
                    <span className="inline-block w-3 h-3 text-center text-[9px] leading-3 rounded-sm bg-emerald-500/20 text-emerald-300 font-semibold">ع</span>
                    {t('بالعربية', 'Arabic')}
                  </label>
                  <textarea
                    value={blockOtherNotes}
                    onChange={(e) => setBlockOtherNotes(e.target.value.slice(0, 500))}
                    rows={3}
                    placeholder={t('اكتب سبب الحظر بالعربية…', 'اكتب سبب الحظر بالعربية…')}
                    className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-white text-sm"
                    dir="rtl"
                    data-testid="cgc-block-other-notes-ar"
                  />
                  <p className="text-[10px] text-white/40 text-end">{blockOtherNotes.length}/500</p>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-white/50 flex items-center gap-1">
                    <span className="inline-block w-3 h-3 text-center text-[9px] leading-3 rounded-sm bg-sky-500/20 text-sky-300 font-semibold">EN</span>
                    {t('بالإنجليزية', 'English')}
                  </label>
                  <textarea
                    value={blockOtherNotesEn}
                    onChange={(e) => setBlockOtherNotesEn(e.target.value.slice(0, 500))}
                    rows={3}
                    placeholder={t('Reason in English…', 'Reason in English…')}
                    className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-white text-sm"
                    dir="ltr"
                    data-testid="cgc-block-other-notes-en"
                  />
                  <p className="text-[10px] text-white/40 text-end">{blockOtherNotesEn.length}/500</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setBlockingUser(null); setBlockReasonId(''); setBlockOtherNotes(''); setBlockOtherNotesEn(''); }}
              className="border-white/10 text-white/70"
              data-testid="cgc-block-cancel-btn"
            >
              <X className="h-3.5 w-3.5 me-1" />
              {t('إلغاء', 'Cancel')}
            </Button>
            <Button
              onClick={submitBlock}
              disabled={!blockReasonId || (isOtherSelected && !blockOtherNotes.trim() && !blockOtherNotesEn.trim()) || !!busy}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="cgc-block-submit-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 me-1 animate-spin" /> : <Ban className="h-3.5 w-3.5 me-1" />}
              {t('تأكيد الحظر', 'Confirm Block')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
